import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, RefreshCw, Mail, Copy } from "lucide-react";
import { BrandLogo } from "@/components/site/BrandLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "delhigence_admin_token";

const formatDate = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const AdminLogin = ({ onAuthed }) => {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!pw.trim()) return;
    setLoading(true);
    try {
      await axios.get(`${API}/admin/me`, {
        headers: { Authorization: `Bearer ${pw.trim()}` },
      });
      sessionStorage.setItem(TOKEN_KEY, pw.trim());
      onAuthed(pw.trim());
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Wrong password.");
      } else {
        toast.error("Could not sign in. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="flex min-h-screen items-center justify-center bg-[color:var(--ivory)] px-6"
    >
      <form
        onSubmit={onSubmit}
        className="w-full max-w-md rounded-2xl border border-[color:var(--rule-strong)] bg-[color:var(--paper)] p-8 shadow-[0_30px_80px_-50px_rgba(14,31,27,0.35)] sm:p-10"
      >
        <BrandLogo className="h-10" tone="ink" testId="admin-login-logo" />
        <div className="mt-8">
          <div className="eyebrow mb-3">/ Admin sign-in</div>
          <h1 className="font-display text-2xl text-[color:var(--ink)] sm:text-3xl">
            Delhigence CMS
          </h1>
          <p className="mt-2 text-sm text-[color:var(--ink-3)]">
            Enter the shared admin password to view inbound leads.
          </p>
        </div>

        <label className="font-mono-accent mt-8 block text-[11px] uppercase tracking-[0.22em] text-[color:var(--muted)]">
          Admin password
        </label>
        <input
          data-testid="admin-password"
          type="password"
          autoFocus
          value={pw}
          onChange={(e) => setPw(e.target.value)}
          className="mt-2 w-full rounded-md border border-[color:var(--rule-strong)] bg-[color:var(--ivory)] px-3 py-2.5 text-sm text-[color:var(--ink)] outline-none placeholder:text-[color:var(--muted-2)] focus:border-[color:var(--emerald)] focus:ring-1 focus:ring-[color:var(--emerald)]"
          placeholder="••••••••••••••"
        />

        <button
          type="submit"
          data-testid="admin-signin-button"
          disabled={loading}
          className="mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full bg-[color:var(--emerald)] px-6 py-3 text-sm font-semibold text-[color:var(--paper)] transition-all hover:bg-[color:var(--ink)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {loading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> Signing in…
            </>
          ) : (
            "Sign in"
          )}
        </button>
      </form>
    </div>
  );
};

const LeadsTable = ({ leads, onDelete, deletingId }) => {
  const copyEmail = async (email) => {
    try {
      await navigator.clipboard.writeText(email);
      toast.success("Email copied.");
    } catch {
      toast.error("Could not copy email.");
    }
  };

  if (leads.length === 0) {
    return (
      <div
        data-testid="admin-empty"
        className="rounded-2xl border border-[color:var(--rule-strong)] bg-[color:var(--paper)] p-12 text-center text-[color:var(--ink-3)]"
      >
        No leads yet. Contact form submissions will appear here.
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-2xl border border-[color:var(--rule-strong)] bg-[color:var(--paper)]">
      <div className="hidden grid-cols-[160px_1fr_1fr_140px_56px] items-center gap-4 border-b border-[color:var(--rule)] px-6 py-4 font-mono-accent text-[10px] uppercase tracking-[0.22em] text-[color:var(--muted)] md:grid">
        <span>Received</span>
        <span>Name · Email · Company</span>
        <span>Message</span>
        <span>Interest</span>
        <span className="text-right">Actions</span>
      </div>

      <ul data-testid="admin-leads-list">
        {leads.map((l) => (
          <li
            key={l.id}
            data-testid={`admin-lead-${l.id}`}
            className="grid grid-cols-1 gap-4 border-b border-[color:var(--rule)] px-6 py-5 last:border-b-0 md:grid-cols-[160px_1fr_1fr_140px_56px]"
          >
            <div className="font-mono-accent text-[11px] uppercase tracking-[0.18em] text-[color:var(--muted)]">
              {formatDate(l.created_at)}
            </div>
            <div>
              <div className="font-display text-base text-[color:var(--ink)]">
                {l.name}
              </div>
              <div className="mt-1 flex items-center gap-2 text-[13px] text-[color:var(--ink-3)]">
                <Mail className="h-3.5 w-3.5 text-[color:var(--emerald)]" />
                <a
                  href={`mailto:${l.email}`}
                  className="hover:text-[color:var(--emerald)]"
                >
                  {l.email}
                </a>
                <button
                  type="button"
                  onClick={() => copyEmail(l.email)}
                  className="rounded p-1 text-[color:var(--muted)] hover:text-[color:var(--emerald)]"
                  aria-label="Copy email"
                >
                  <Copy className="h-3.5 w-3.5" />
                </button>
              </div>
              {l.company ? (
                <div className="mt-1 text-[12px] text-[color:var(--muted)]">
                  {l.company}
                </div>
              ) : null}
            </div>
            <div className="whitespace-pre-wrap text-[14px] leading-relaxed text-[color:var(--ink-2)]">
              {l.message}
            </div>
            <div className="text-[12px] text-[color:var(--ink-3)]">
              {l.interest ? (
                <span className="inline-flex rounded-full border border-[color:var(--emerald)]/25 bg-[color:var(--emerald)]/[0.06] px-2 py-1 text-[11px] uppercase tracking-wider text-[color:var(--emerald)]">
                  {l.interest}
                </span>
              ) : (
                <span className="text-[color:var(--muted-2)]">—</span>
              )}
            </div>
            <div className="flex justify-end">
              <button
                type="button"
                onClick={() => onDelete(l.id)}
                disabled={deletingId === l.id}
                data-testid={`admin-delete-${l.id}`}
                className="rounded-md p-2 text-[color:var(--muted)] transition-colors hover:bg-[color:var(--ivory)] hover:text-red-600 disabled:opacity-50"
                aria-label="Delete lead"
              >
                {deletingId === l.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchLeads = useCallback(async (tok) => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/leads`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setLeads(data || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load leads.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) fetchLeads(token);
  }, [token, fetchLeads]);

  const onDelete = async (id) => {
    if (!window.confirm("Delete this lead permanently?")) return;
    setDeletingId(id);
    try {
      await axios.delete(`${API}/admin/leads/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success("Lead deleted.");
    } catch {
      toast.error("Could not delete lead.");
    } finally {
      setDeletingId(null);
    }
  };

  const onLogout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken(null);
    setLeads([]);
    toast.success("Signed out.");
  };

  if (!token) {
    return <AdminLogin onAuthed={(t) => setToken(t)} />;
  }

  return (
    <div
      data-testid="admin-page"
      className="min-h-screen bg-[color:var(--ivory)] pb-24 pt-10"
    >
      <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
        <header className="flex flex-col items-start justify-between gap-6 border-b border-[color:var(--rule)] pb-8 sm:flex-row sm:items-center">
          <div className="flex items-center gap-5">
            <BrandLogo className="h-7 sm:h-8" tone="ink" />
            <span className="font-mono-accent rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)] px-3 py-1 text-[10px] uppercase tracking-[0.22em] text-[color:var(--emerald)]">
              CMS
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="admin-refresh"
              onClick={() => fetchLeads(token)}
              disabled={loading}
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)] px-4 py-2 text-sm text-[color:var(--ink-2)] hover:bg-[color:var(--ivory-2)] disabled:opacity-50"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </button>
            <button
              type="button"
              data-testid="admin-logout"
              onClick={onLogout}
              className="inline-flex items-center gap-2 rounded-full border border-[color:var(--rule-strong)] bg-[color:var(--paper)] px-4 py-2 text-sm text-[color:var(--ink-2)] hover:bg-[color:var(--ivory-2)]"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </header>

        <div className="mt-10 mb-8 flex items-end justify-between">
          <div>
            <div className="eyebrow mb-3">/ Inbound leads</div>
            <h1 className="font-display text-3xl text-[color:var(--ink)] sm:text-4xl">
              {leads.length} {leads.length === 1 ? "lead" : "leads"}
            </h1>
          </div>
        </div>

        {loading && leads.length === 0 ? (
          <div className="flex items-center justify-center rounded-2xl border border-[color:var(--rule-strong)] bg-[color:var(--paper)] py-16 text-[color:var(--ink-3)]">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Loading leads…
          </div>
        ) : (
          <LeadsTable
            leads={leads}
            onDelete={onDelete}
            deletingId={deletingId}
          />
        )}
      </div>
    </div>
  );
}
